import React, { useState, useEffect } from "react";
import { LayoutDashboard, Building2, Cctv, RefreshCw, ChevronLeft, Wifi, WifiOff, AlertCircle, Search, Activity, Bell } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import baseapi from "../../services/baseapi";
import { MiniMap } from "./MiniMap";
import { CameraListSidebar } from "./CameraListSidebar";
import { EventListSidebar } from "./EventListSidebar";

import { useClientLevel } from "../../hooks/useClientLevel";
import { type LocationIds } from "../../components/LocationSelectorSection";

interface District {
    district_id: number;
    district_name: string;
    state_id: number;
    state_name: string;
    station_count: number;
    cameras?: {
        total: number;
        online: number;
        offline: number;
        health_pct: number;
    };
    events?: {
        total: number;
        open: number;
        closed: number;
    };
}

interface Station {
    client_id: number;
    client_name: string;
    cameras?: {
        total: number;
        online: number;
        offline: number;
        health_pct: number;
    };
    events?: {
        total: number;
        open: number;
        closed: number;
    };
}

const DistrictOverviewDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [districts, setDistricts] = useState<District[]>([]);
    const [stations, setStations] = useState<Station[]>([]);
    const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(null);
    const [selectedDistrictName, setSelectedDistrictName] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [clientsData, setClientsData] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");

    // Calculate header stats dynamically based on the current level/view
    const headerStats = React.useMemo(() => {
        const list = !selectedDistrictId ? districts : stations;
        return list.reduce(
            (acc, item) => {
                acc.total += item.cameras?.total ?? 0;
                acc.online += item.cameras?.online ?? 0;
                acc.offline += item.cameras?.offline ?? 0;
                acc.events += item.events?.total ?? 0;
                return acc;
            },
            { total: 0, online: 0, offline: 0, events: 0 }
        );
    }, [selectedDistrictId, districts, stations]);

    const level = useClientLevel();
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
    const [cameraListStatus, setCameraListStatus] = useState<"all" | "active" | "offline">("all");
    const [sidebarLocationIds, setSidebarLocationIds] = useState<LocationIds>({
        regionId: null,
        stateId: null,
        districtId: null,
        townId: null,
        siteId: null
    });

    const handleOpenSidebar = (
        e: React.MouseEvent,
        districtId?: number,
        status: "all" | "active" | "offline" = "all",
        siteId?: number
    ) => {
        e.stopPropagation();
        setCameraListStatus(status);

        const ids: LocationIds = {
            regionId: searchParams.get('region_id') ? Number(searchParams.get('region_id')) : 2,
            stateId: searchParams.get('state_id') ? Number(searchParams.get('state_id')) : 1,
            districtId: districtId || (selectedDistrictId ? Number(selectedDistrictId) : null),
            townId: null,
            siteId: siteId || null,
        };

        setSidebarLocationIds(ids);
        setIsSidebarOpen(true);
    };
    const [isEventSidebarOpen, setIsEventSidebarOpen] = useState<boolean>(false);
    const [eventListStatus, setEventListStatus] = useState<"all" | "open" | "closed">("all");
    const [sidebarEventLocationIds, setSidebarEventLocationIds] = useState<LocationIds>({
        regionId: null,
        stateId: null,
        districtId: null,
        townId: null,
        siteId: null
    });

    const handleOpenEventSidebar = (
        e: React.MouseEvent,
        districtId?: number,
        status: "all" | "open" | "closed" = "all",
        siteId?: number
    ) => {
        e.stopPropagation();
        setEventListStatus(status);

        const ids: LocationIds = {
            regionId: searchParams.get('region_id') ? Number(searchParams.get('region_id')) : 2,
            stateId: searchParams.get('state_id') ? Number(searchParams.get('state_id')) : 1,
            districtId: districtId || (selectedDistrictId ? Number(selectedDistrictId) : null),
            townId: null,
            siteId: siteId || null,
        };

        setSidebarEventLocationIds(ids);
        setIsEventSidebarOpen(true);
    };



    const fetchDistrictOverview = async (districtId?: number) => {
        try {
            setLoading(true);
            const params: any = {};
            if (districtId) params.district_id = districtId;
            const res = await baseapi.get('/dashboard/district_overview', { params });
            if (res?.data?.status) {
                const respData = res.data.data;
                if (respData.mode === 'districts') {
                    setDistricts(respData.districts || []);
                    setStations([]);
                } else if (respData.mode === 'district_stations') {
                    setStations(respData.stations || []);
                    if (respData.district) {
                        setSelectedDistrictName(respData.district.district_name);
                        setSelectedDistrictId(respData.district.district_id);
                    }
                }
            }
        } catch (error) {
            console.error("Failed to fetch district overview:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchClients = async (districtId?: number) => {
        try {
            const queryObj: any = { region_id: 2, state_id: 1 };
            if (districtId) {
                queryObj.district_id = districtId;
            }
            const response = await baseapi.get('/clients', {
                params: {
                    limit: 1000,
                    query: JSON.stringify(queryObj)
                }
            });
            if (response.data?.status && response.data?.data) {
                const parsed = (response.data.data || []).map((c: any) => {
                    let lat = 0, lng = 0;
                    if (c.latitude && c.longitude) {
                        lat = Number(c.latitude);
                        lng = Number(c.longitude);
                    } else if (c.client_lat && c.client_lng) {
                        lat = Number(c.client_lat);
                        lng = Number(c.client_lng);
                    }
                    return {
                        ...c,
                        coordinate: [lat, lng] as [number, number],
                        is_offline: !!c.is_offline
                    };
                }).filter((c: any) => c.coordinate[0] !== 0 && c.coordinate[1] !== 0);
                setClientsData(parsed);
            }
        } catch (e) {
            console.error("Failed to fetch map client list:", e);
        }
    };

    useEffect(() => {
        const did = searchParams.get('district_id');
        if (did) {
            fetchDistrictOverview(Number(did));
            fetchClients(Number(did));
        } else {
            setSelectedDistrictId(null);
            setSelectedDistrictName("");
            fetchDistrictOverview();
            fetchClients();
        }
    }, [searchParams]);

    const handleDistrictSelect = (districtId: number) => {
        setSearchParams({ district_id: String(districtId) });
    };

    const handleBackToDistricts = () => {
        setSearchParams({});
    };

    const handleStationSelect = (clientId: number, clientName: string) => {
        const did = selectedDistrictId ? `&district_id=${selectedDistrictId}` : '';
        const dname = selectedDistrictName ? `&district_name=${encodeURIComponent(selectedDistrictName)}` : '';
        navigate(`/station-dashboard?client_id=${clientId}&client_name=${encodeURIComponent(clientName)}${did}${dname}`);
    };

    const handleGoToCameraDashboard = () => {
        const did = selectedDistrictId ? `?district_id=${selectedDistrictId}` : '';
        const dname = selectedDistrictName ? `&district_name=${encodeURIComponent(selectedDistrictName)}` : '';
        navigate(`/station-dashboard${did}${dname}`);
    };

    const handleRefresh = () => {
        fetchDistrictOverview(selectedDistrictId || undefined);
    };

    const filteredDistricts = districts.filter(d =>
        d.district_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const filteredStations = stations.filter(s =>
        s.client_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getStatusDetails = (total: number, offline: number) => {
        if (total === 0) {
            return { text: "IDLE", colorClass: "text-slate-500 bg-slate-50 border-slate-200" };
        }
        const healthPct = ((total - offline) / total) * 100;
        if (healthPct > 75) {
            return { text: "HEALTHY", colorClass: "text-emerald-600 bg-emerald-50 border-emerald-100" };
        }
        if (healthPct > 0) {
            return { text: "ATTENTION REQUIRED", colorClass: "text-amber-600 bg-amber-50 border-amber-100" };
        }
        return { text: "CRITICAL", colorClass: "text-rose-600 bg-rose-50 border-rose-100 animate-pulse" };
    };

    return (
        <div className="flex-1 flex flex-col bg-slate-50 overflow-hidden font-sans pb-20">
            {/* Header */}
            <div className="min-h-12 py-2 bg-white border-b border-slate-200 flex items-center justify-between px-6 shrink-0 relative z-10">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#422568] rounded-xl text-white shadow-lg shadow-[#422568]/20">
                        <LayoutDashboard className="w-5 h-5" />
                    </div>
                    <div>
                        <h1 className="text-xs font-black text-slate-900 uppercase tracking-widest leading-none">District Overview</h1>
                        <p className="text-[9px] font-bold text-slate-800 uppercase tracking-wider mt-1">Multi-site central surveillance management</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={handleGoToCameraDashboard}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#422568] hover:bg-[#422568]/90 text-white rounded-lg transition-all font-black text-[9px] uppercase shadow-md shadow-[#422568]/20"
                    >
                        <LayoutDashboard className="w-3.5 h-3.5" /> Overview Dashboard
                    </button>
                    {selectedDistrictId && (
                        <button
                            onClick={handleBackToDistricts}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-slate-600 hover:text-[#422568] transition-all font-black text-[9px] uppercase shadow-sm animate-in duration-300"
                        >
                            <ChevronLeft className="w-3.5 h-3.5 text-[#422568]" /> Back to Districts
                        </button>
                    )}
                    <button
                        onClick={handleRefresh}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-[#422568]/5 border border-slate-200 hover:border-[#422568]/25 rounded-lg text-slate-600 hover:text-[#422568] transition-all font-black text-[9px] uppercase shadow-sm"
                    >
                        <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className={`flex-1 overflow-y-auto p-6 custom-scroll ${loading ? 'flex flex-col' : ''}`}>
                {loading ? (
                    <div className="flex-1 flex flex-col items-center justify-center py-32 gap-3 min-h-[50vh]">
                        <div className="relative">
                            <div className="w-12 h-12 border-4 border-[#422568]/10 border-t-[#422568] rounded-full animate-spin" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <LayoutDashboard className="w-4 h-4 text-[#422568] animate-pulse" />
                            </div>
                        </div>
                        <span className="text-[8px] font-black text-slate-450 uppercase tracking-[0.3em] animate-pulse">Syncing Intelligence</span>
                    </div>
                ) : (
                    <>
                        {/* Top Stats Cards Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                            {/* Total Cameras Card */}
                            <div
                                onClick={(e) => handleOpenSidebar(e, undefined, "all")}
                                className="bg-white px-3.5 py-2.5 rounded-xl border border-slate-200/85 shadow-sm flex items-center justify-between hover:shadow-md cursor-pointer transition-all group hover:-translate-y-0.5"
                            >
                                <div className="min-w-0">
                                    <span className="text-[8px] font-black text-slate-800 uppercase tracking-wider block">Total Cameras</span>
                                    <h3 className="text-base font-black text-slate-800 mt-0.5 leading-none group-hover:text-[#422568] transition-colors">
                                        {headerStats.total.toLocaleString()}
                                    </h3>
                                </div>
                                <div className="p-2 rounded-lg bg-[#422568] text-white transition-colors group-hover:bg-[#422568] group-hover:text-white flex-shrink-0">
                                    <Cctv className="w-4 h-4" />
                                </div>
                            </div>

                            {/* Online Cameras Card */}
                            <div
                                onClick={(e) => handleOpenSidebar(e, undefined, "active")}
                                className="bg-white px-3.5 py-2.5 rounded-xl border border-slate-200/85 shadow-sm flex items-center justify-between hover:shadow-md cursor-pointer transition-all group hover:-translate-y-0.5"
                            >
                                <div className="min-w-0">
                                    <span className="text-[8px] font-black text-slate-800 uppercase tracking-wider block">Online</span>
                                    <h3 className="text-base font-black text-slate-800 mt-0.5 leading-none group-hover:text-emerald-600 transition-colors">
                                        {headerStats.online.toLocaleString()}
                                    </h3>
                                    <span className="text-[8px] text-slate-800 font-bold mt-0.5 block truncate">
                                        {headerStats.total > 0 ? Math.round((headerStats.online / headerStats.total) * 100) : 0}% healthy
                                    </span>
                                </div>
                                <div className="p-2 rounded-lg bg-emerald-500 text-white transition-colors group-hover:bg-emerald-500 group-hover:text-white flex-shrink-0">
                                    <Wifi className="w-4 h-4" />
                                </div>
                            </div>

                            {/* Offline Cameras Card */}
                            <div
                                onClick={(e) => handleOpenSidebar(e, undefined, "offline")}
                                className="bg-white px-3.5 py-2.5 rounded-xl border border-slate-200/85 shadow-sm flex items-center justify-between hover:shadow-md cursor-pointer transition-all group hover:-translate-y-0.5"
                            >
                                <div className="min-w-0">
                                    <span className="text-[8px] font-black text-slate-800 uppercase tracking-wider block">Offline</span>
                                    <h3 className="text-base font-black text-slate-800 mt-0.5 leading-none group-hover:text-rose-600 transition-colors">
                                        {headerStats.offline.toLocaleString()}
                                    </h3>
                                    <span className="text-[8px] text-slate-800 font-bold mt-0.5 block truncate">
                                        {headerStats.offline > 0 ? "Needs attention" : "System Healthy"}
                                    </span>
                                </div>
                                <div className="p-2 rounded-lg bg-rose-500 text-white transition-colors group-hover:bg-rose-500 group-hover:text-white flex-shrink-0">
                                    <WifiOff className="w-4 h-4" />
                                </div>
                            </div>

                            {/* Active Events Card */}
                            <div
                                onClick={(e) => handleOpenEventSidebar(e, undefined, "all")}
                                className="bg-white px-3.5 py-2.5 rounded-xl border border-slate-200/85 shadow-sm flex items-center justify-between hover:shadow-md cursor-pointer transition-all group hover:-translate-y-0.5"
                            >
                                <div className="min-w-0">
                                    <span className="text-[8px] font-black text-slate-800 uppercase tracking-wider block">Events</span>
                                    <h3 className="text-base font-black text-slate-800 mt-0.5 leading-none group-hover:text-amber-600 transition-colors">
                                        {headerStats.events.toLocaleString()}
                                    </h3>
                                    {/* <span className="text-[8px] text-slate-800 font-bold mt-0.5 block truncate">
                                        
                                    </span> */}
                                </div>
                                <div className="p-2 rounded-lg bg-amber-500 text-white transition-colors group-hover:bg-amber-500 group-hover:text-white flex-shrink-0">
                                    <Bell className="w-4 h-4" />
                                </div>
                            </div>
                        </div>

                        {!selectedDistrictId ? (
                            /* DISTRICT LIST MODE */
                            <div className="space-y-6">
                                <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-4">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-base font-black text-slate-855 tracking-tight uppercase">Districts</h2>
                                        <span className="text-[10px] font-bold bg-[#422568]/10 text-[#422568] px-2 py-0.5 rounded-full uppercase tracking-wider">
                                            {filteredDistricts.length} of {districts.length}
                                        </span>
                                    </div>
                                    <div className="relative w-64">
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search district..."
                                            className="w-full text-xs pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#422568] text-slate-700 shadow-sm"
                                        />
                                        <Search className="w-3.5 h-3.5 text-slate-800 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {filteredDistricts.map((d) => {
                                        const totalCams = d.cameras?.total ?? 0;
                                        const online = d.cameras?.online ?? 0;
                                        const offline = d.cameras?.offline ?? 0;
                                        const totalEvents = d.events?.total ?? 0;
                                        const healthPct = d.cameras?.health_pct ?? 0;
                                        const status = getStatusDetails(totalCams, offline);

                                        return (
                                            <div
                                                key={d.district_id}
                                                onClick={() => handleDistrictSelect(d.district_id)}
                                                className="bg-white border border-slate-200 hover:border-[#422568]/40 rounded-xl p-4 cursor-pointer transition-all hover:shadow-lg shadow-sm hover:-translate-y-0.5 group flex flex-col gap-3 relative overflow-hidden"
                                            >
                                                {/* Header */}
                                                <div className="flex justify-between items-start border-b border-slate-100 pb-3">
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        <div className="p-2 bg-[#422568]/5 text-[#422568] rounded-xl group-hover:bg-[#422568] group-hover:text-white transition-all shadow-sm">
                                                            <Building2 className="w-4 h-4 flex-shrink-0" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <h3 className="text-xs font-black text-slate-800 uppercase tracking-wide truncate group-hover:text-[#422568] transition-colors">{d.district_name}</h3>
                                                            <p className="text-[9px] font-bold text-slate-405 mt-0.5 uppercase tracking-wider">
                                                                {d.station_count} police station{d.station_count !== 1 ? 's' : ''}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <span className={`text-[8px] font-black px-2 py-0.5 rounded border uppercase tracking-wider ${status.colorClass}`}>{status.text}</span>
                                                </div>

                                                {/* Camera Health Section */}
                                                <div className="space-y-1">
                                                    <div className="flex justify-between items-center text-[9px] font-black text-slate-800 uppercase tracking-wide">
                                                        <span className="flex items-center gap-1">
                                                            <Activity className="w-3 h-3 text-slate-800" />
                                                            Camera Health
                                                        </span>
                                                        <span className={healthPct > 0 ? "text-slate-700" : "text-slate-800"}>
                                                            {healthPct}%
                                                        </span>
                                                    </div>
                                                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
                                                        <div
                                                            className={`h-full rounded-full transition-all duration-500 ${totalCams === 0
                                                                ? 'bg-slate-300'
                                                                : healthPct >= 90
                                                                    ? 'bg-emerald-500'
                                                                    : 'bg-amber-500'
                                                                }`}
                                                            style={{ width: `${healthPct}%` }}
                                                        ></div>
                                                    </div>
                                                </div>

                                                {/* Stats Columns: Total, Online, Offline, Events */}
                                                <div className="grid grid-cols-4 gap-1.5 text-[8px] font-black uppercase mt-1">
                                                    <div
                                                        onClick={(e) => handleOpenSidebar(e, d.district_id, "all")}
                                                        className="bg-slate-50 border border-slate-100 rounded-lg p-1.5 flex flex-col items-center justify-center gap-1 hover:bg-slate-100 hover:border-slate-350 cursor-pointer transition-all"
                                                    >
                                                        <div className="flex items-center gap-0.5 text-slate-800">
                                                            <Cctv className="w-2.5 h-2.5 text-slate-500" />
                                                            <span className="text-[7px] text-slate-500 tracking-wider">Total</span>
                                                        </div>
                                                        <span className="text-slate-800 text-xs font-black leading-none mt-0.5">{totalCams}</span>
                                                    </div>
                                                    <div
                                                        onClick={(e) => handleOpenSidebar(e, d.district_id, "active")}
                                                        className="bg-emerald-50/50 border border-emerald-100/80 rounded-lg p-1.5 flex flex-col items-center justify-center gap-1 hover:bg-emerald-100/50 hover:border-emerald-300 cursor-pointer transition-all"
                                                    >
                                                        <div className="flex items-center gap-0.5 text-emerald-600">
                                                            <Wifi className="w-2.5 h-2.5 text-emerald-500" />
                                                            <span className="text-[7px] text-emerald-600 tracking-wider">Online</span>
                                                        </div>
                                                        <span className="text-emerald-700 text-xs font-black leading-none mt-0.5">{online}</span>
                                                    </div>
                                                    <div
                                                        onClick={(e) => handleOpenSidebar(e, d.district_id, "offline")}
                                                        className="bg-rose-50/50 border border-rose-100/80 rounded-lg p-1.5 flex flex-col items-center justify-center gap-1 hover:bg-rose-100/50 hover:border-rose-300 cursor-pointer transition-all"
                                                    >
                                                        <div className="flex items-center gap-0.5 text-rose-650">
                                                            <WifiOff className="w-2.5 h-2.5 text-rose-500" />
                                                            <span className="text-[7px] text-rose-600 tracking-wider">Offline</span>
                                                        </div>
                                                        <span className="text-rose-700 text-xs font-black leading-none mt-0.5">{offline}</span>
                                                    </div>
                                                    <div
                                                        onClick={(e) => handleOpenEventSidebar(e, d.district_id, "all")}
                                                        className="bg-slate-50 border border-slate-100 rounded-lg p-1.5 flex flex-col items-center justify-center gap-1 hover:bg-[#422568]/5 hover:border-[#422568]/30 cursor-pointer transition-all group/events"
                                                    >
                                                        <div className="flex items-center gap-0.5 text-slate-800 group-hover/events:text-[#422568]">
                                                            <AlertCircle className="w-2.5 h-2.5 text-slate-500 group-hover/events:text-[#422568]" />
                                                            <span className="text-[7px] text-slate-500 tracking-wider">Events</span>
                                                        </div>
                                                        <span className="text-slate-800 text-xs font-black leading-none mt-0.5 group-hover/events:text-[#422568]">{totalEvents}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            /* STATION LIST MODE FOR SELECTED DISTRICT */
                            <div className="space-y-6">
                                <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-4">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-base font-black text-slate-800 tracking-tight uppercase">{selectedDistrictName} District Overview</h2>
                                        <span className="text-[10px] font-bold bg-[#422568]/10 text-[#422568] px-2 py-0.5 rounded-full uppercase tracking-wider">
                                            {filteredStations.length} of {stations.length}
                                        </span>
                                    </div>
                                    <div className="relative w-64">
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search station..."
                                            className="w-full text-xs pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#422568] text-slate-700 shadow-sm"
                                        />
                                        <Search className="w-3.5 h-3.5 text-slate-800 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {filteredStations.map((s) => {
                                        const totalCams = s.cameras?.total ?? 0;
                                        const online = s.cameras?.online ?? 0;
                                        const offline = s.cameras?.offline ?? 0;
                                        const totalEvents = s.events?.total ?? 0;
                                        const healthPct = totalCams > 0 ? Math.round((online / totalCams) * 100) : 0;
                                        const status = getStatusDetails(totalCams, offline);

                                        return (
                                            <div
                                                key={s.client_id}
                                                onClick={() => handleStationSelect(s.client_id, s.client_name)}
                                                className="bg-white border border-slate-200 hover:border-[#422568]/45 rounded-xl p-4 cursor-pointer transition-all hover:shadow-lg shadow-sm hover:-translate-y-0.5 group flex flex-col gap-3 relative overflow-hidden"
                                            >
                                                {/* Card Header */}
                                                <div className="flex justify-between items-start border-b border-slate-100 pb-3">
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        <div className="p-2 bg-[#422568]/5 text-[#422568] rounded-xl group-hover:bg-[#422568] group-hover:text-white transition-all shadow-sm">
                                                            <Cctv className="w-4 h-4 flex-shrink-0" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <h3 className="text-xs font-black text-slate-850 uppercase tracking-wide truncate group-hover:text-[#422568] transition-colors">{s.client_name}</h3>
                                                            <p className="text-[9px] font-bold text-slate-405 mt-0.5 uppercase tracking-wider">
                                                                Active surveillance site
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <span className={`text-[8px] font-black px-2 py-0.5 rounded border uppercase tracking-wider ${status.colorClass}`}>{status.text}</span>
                                                </div>

                                                {/* Camera Health Section */}
                                                <div className="space-y-1">
                                                    <div className="flex justify-between items-center text-[9px] font-black text-slate-800 uppercase tracking-wide">
                                                        <span className="flex items-center gap-1">
                                                            <Activity className="w-3 h-3 text-slate-800" />
                                                            Camera Health
                                                        </span>
                                                        <span className={healthPct > 0 ? "text-slate-700" : "text-slate-800"}>
                                                            {healthPct}%
                                                        </span>
                                                    </div>
                                                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
                                                        <div
                                                            className={`h-full rounded-full transition-all duration-500 ${totalCams === 0
                                                                ? 'bg-slate-300'
                                                                : healthPct >= 90
                                                                    ? 'bg-emerald-500'
                                                                    : 'bg-amber-500'
                                                                }`}
                                                            style={{ width: `${healthPct}%` }}
                                                        ></div>
                                                    </div>
                                                </div>

                                                {/* Stats Columns: Total, Online, Offline, Events */}
                                                <div className="grid grid-cols-4 gap-1.5 text-[8px] font-black uppercase mt-1">
                                                    <div
                                                        onClick={(e) => handleOpenSidebar(e, undefined, "all", s.client_id)}
                                                        className="bg-slate-50 border border-slate-100 rounded-lg p-1.5 flex flex-col items-center justify-center gap-1 hover:bg-slate-100 hover:border-slate-350 cursor-pointer transition-all"
                                                    >
                                                        <div className="flex items-center gap-0.5 text-slate-800">
                                                            <Cctv className="w-2.5 h-2.5 text-slate-500" />
                                                            <span className="text-[7px] text-slate-500 tracking-wider">Total</span>
                                                        </div>
                                                        <span className="text-slate-800 text-xs font-black leading-none mt-0.5">{totalCams}</span>
                                                    </div>
                                                    <div
                                                        onClick={(e) => handleOpenSidebar(e, undefined, "active", s.client_id)}
                                                        className="bg-emerald-50/50 border border-emerald-100/80 rounded-lg p-1.5 flex flex-col items-center justify-center gap-1 hover:bg-emerald-100/50 hover:border-emerald-300 cursor-pointer transition-all"
                                                    >
                                                        <div className="flex items-center gap-0.5 text-emerald-600">
                                                            <Wifi className="w-2.5 h-2.5 text-emerald-500" />
                                                            <span className="text-[7px] text-emerald-600 tracking-wider">Online</span>
                                                        </div>
                                                        <span className="text-emerald-700 text-xs font-black leading-none mt-0.5">{online}</span>
                                                    </div>
                                                    <div
                                                        onClick={(e) => handleOpenSidebar(e, undefined, "offline", s.client_id)}
                                                        className="bg-rose-50/50 border border-rose-100/80 rounded-lg p-1.5 flex flex-col items-center justify-center gap-1 hover:bg-rose-100/50 hover:border-rose-300 cursor-pointer transition-all"
                                                    >
                                                        <div className="flex items-center gap-0.5 text-rose-600">
                                                            <WifiOff className="w-2.5 h-2.5 text-rose-505" />
                                                            <span className="text-[7px] text-rose-650 tracking-wider">Offline</span>
                                                        </div>
                                                        <span className="text-rose-700 text-xs font-black leading-none mt-0.5">{offline}</span>
                                                    </div>
                                                    <div
                                                        onClick={(e) => handleOpenEventSidebar(e, undefined, "all", s.client_id)}
                                                        className="bg-slate-50 border border-slate-100 rounded-lg p-1.5 flex flex-col items-center justify-center gap-1 hover:bg-[#422568]/5 hover:border-[#422568]/30 cursor-pointer transition-all group/events"
                                                    >
                                                        <div className="flex items-center gap-0.5 text-slate-800 group-hover/events:text-[#422568]">
                                                            <AlertCircle className="w-2.5 h-2.5 text-slate-500 group-hover/events:text-[#422568]" />
                                                            <span className="text-[7px] text-slate-500 tracking-wider">Events</span>
                                                        </div>
                                                        <span className="text-slate-800 text-xs font-black leading-none mt-0.5 group-hover/events:text-[#422568]">{totalEvents}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Deployment Site Map Section at the bottom */}
                        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col gap-3 mt-4">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                <h2 className="text-xs font-black text-slate-800 uppercase tracking-wider">Deployment Site Map</h2>
                                {selectedDistrictId && (
                                    <span className="text-[9px] font-black bg-[#422568]/10 text-[#422568] px-2 py-0.5 rounded uppercase tracking-wider">
                                        {selectedDistrictName}
                                    </span>
                                )}
                            </div>
                            <div className="h-[380px] w-full relative z-0">
                                <MiniMap clients={clientsData} selectedDistrictName={selectedDistrictName || undefined} />
                            </div>
                        </div>
                    </>
                )}
            </div>

            <CameraListSidebar
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                initialStatus={cameraListStatus}
                locationIds={sidebarLocationIds}
                level={level}
            />

            <EventListSidebar
                isOpen={isEventSidebarOpen}
                onClose={() => setIsEventSidebarOpen(false)}
                initialStatus={eventListStatus}
                locationIds={sidebarEventLocationIds}
                level={level}
            />
        </div>
    );
};

export default DistrictOverviewDashboard;
